import { parseSelectFilterOptions } from './search';
import { resolvePriceRangeRowForGuestRange } from './capacityFieldUtils';

/**
 * Precio hora mostrado al huésped = precio del anuncio (subunidades) + 20% Cocopool.
 * Debe coincidir con server/api-util/cocopoolPricing.js
 */
export const HOURLY_CUSTOMER_MARKUP = 1.2;

export function applyHourlyCustomerPriceSubunits(amountSubunits) {
  if (amountSubunits == null || !Number.isFinite(Number(amountSubunits))) {
    return amountSubunits;
  }
  return Math.round(Number(amountSubunits) * HOURLY_CUSTOMER_MARKUP);
}

/**
 * Recompone, solo para MOSTRAR (nunca para cobrar), el precio con comisión
 * incluida antes de cualquier descuento, y el importe de cada descuento como
 * un % de esa cifra, encadenados en el mismo orden en que el servidor los
 * aplica (p. ej. entre semana → primera reserva). Así, si el bañista ve
 * "1.200 €" como base, un descuento del 20% se ve como el 20% de esos
 * 1.200 €, no de un importe sin comisión que nunca se muestra.
 *
 * Es exacto, no una aproximación: la comisión es un porcentaje fijo, así que
 * "descontar y luego comisionar" y "comisionar y luego descontar" dan el
 * mismo importe final (la multiplicación conmuta) — el importe real cobrado
 * (`server/api-util/lineItems.js`) no cambia, solo cómo se reparte entre las
 * filas mostradas.
 *
 * @param {number} rawBaseAmountSubunits precio base (+ extras) SIN descuentos NI comisión, en subunidades
 * @param {number} commissionPct porcentaje de comisión del cliente (p.ej. 20)
 * @param {Array<number|null|undefined>} discountPercents porcentajes de descuento, en el orden del servidor
 * @returns {{ grossAmount: number, discountAmounts: number[], afterDiscountsAmount: number }}
 */
export function buildCommissionInclusiveDiscountChain(
  rawBaseAmountSubunits,
  commissionPct,
  discountPercents
) {
  const base = Number.isFinite(Number(rawBaseAmountSubunits)) ? Number(rawBaseAmountSubunits) : 0;
  const rate = Number.isFinite(Number(commissionPct)) ? Number(commissionPct) : 0;
  const grossAmount = Math.round(base * (1 + rate / 100));

  let running = grossAmount;
  const discountAmounts = (discountPercents || []).map(pct => {
    const pctNum = Number(pct);
    if (pct == null || !Number.isFinite(pctNum) || pctNum === 0) {
      return 0;
    }
    const amt = Math.round(running * (pctNum / 100));
    running -= amt;
    return amt;
  });

  return { grossAmount, discountAmounts, afterDiscountsAmount: running };
}

/**
 * Seguro de Responsabilidad Civil del anfitrión (espejo de cliente).
 * El servidor es la fuente de verdad del importe cobrado (line item negativo);
 * estos valores se usan solo para mostrar el importe en el modal de aceptación.
 * Deben coincidir con server/api-util/cocopoolPricing.js.
 *
 * IMPORTANTE: `HOST_INSURANCE_FEE_SUBUNITS` = 992 (9,92 € = 8,20 € base + 21%
 * IVA), importe bruto descontado. Debe coincidir con el servidor.
 */
export const HOST_INSURANCE_ENABLED = true;
export const HOST_INSURANCE_FEE_SUBUNITS = 992;
export const HOST_INSURANCE_CURRENCY = 'EUR';

/**
 * Valor del filtro de capacidad en URL (p. ej. `6-10`) → clave que coincide con `priceRange[].quantityRange`.
 */
export function normalizeSearchCapacityGuestRange(raw) {
  if (raw == null || raw === '') {
    return null;
  }
  const s = String(raw).trim();
  if (!s) {
    return null;
  }
  const parts = parseSelectFilterOptions(s);
  const first = parts.find(p => p != null && String(p).trim() !== '');
  return first != null ? String(first).trim() : null;
}

/**
 * Precio a mostrar al huésped en tarjetas / mapa / búsqueda. Siempre incluye el 20% de Cocopool
 * (la comisión del huésped que el servidor añade como line item en la reserva), de modo que el
 * precio mostrado coincide con lo que se paga.
 * Con `searchCapacityGuestRange` (valor del filtro de grupo en /s/) usa el tramo del anuncio que
 * cubre ese grupo, resuelto por intervalos numéricos: los tramos del anuncio no tienen por qué
 * coincidir con los buckets del buscador (ver `resolvePriceRangeRowForGuestRange`). Sin filtro
 * activo —o si ningún tramo llega a ese grupo— se usa el primer tramo (precio "desde"). En ambos
 * casos se aplica el 20%.
 *
 * @param {object} listing
 * @param {string|null|undefined} [searchCapacityGuestRange] valor del query param de capacidad (p. ej. pub_capacity)
 * @returns {{ amount: number, currency: string } | null}
 */
export function getCustomerFacingPriceSubunitsAndCurrency(listing, searchCapacityGuestRange) {
  const attrs = listing?.attributes;
  if (!attrs) {
    return null;
  }
  const { price, publicData } = attrs;
  if (!publicData || publicData.unitType !== 'hour') {
    return null;
  }
  const priceRange = Array.isArray(publicData.priceRange) ? publicData.priceRange : [];
  const guestRangeKey = normalizeSearchCapacityGuestRange(searchCapacityGuestRange);

  let baseSubunits = null;
  let currency = null;

  if (guestRangeKey && priceRange.length > 0) {
    const row = resolvePriceRangeRowForGuestRange(priceRange, guestRangeKey);
    if (row?.amount != null && row?.currency) {
      baseSubunits = Number(row.amount);
      currency = row.currency;
    }
  }

  if (baseSubunits == null && priceRange.length > 0) {
    const first = priceRange[0];
    if (first?.amount != null && first?.currency) {
      baseSubunits = Number(first.amount);
      currency = first.currency;
    }
  }
  if (baseSubunits == null && price?.amount != null && price?.currency) {
    baseSubunits = Number(price.amount);
    currency = price.currency;
  }
  if (baseSubunits == null || !currency) {
    return null;
  }
  return {
    amount: applyHourlyCustomerPriceSubunits(baseSubunits),
    currency,
  };
}

/** Clave estable para comparar si el precio mostrado cambió (incl. +20% en horas y filtro capacidad). */
export function getListingDisplayPriceKey(listing, searchCapacityGuestRange) {
  const attrs = listing?.attributes;
  if (!attrs) {
    return '';
  }
  const cap = normalizeSearchCapacityGuestRange(searchCapacityGuestRange);
  const capSeg = cap ? `|cap:${cap}` : '';
  const cf = getCustomerFacingPriceSubunitsAndCurrency(listing, searchCapacityGuestRange);
  if (cf) {
    return `${cf.amount}|${cf.currency}${capSeg}`;
  }
  const p = attrs.price;
  if (p?.amount != null && p?.currency) {
    return `${p.amount}|${p.currency}${capSeg}`;
  }
  return capSeg || '';
}

/**
 * Reservas externas protegidas: espejo de cliente de `externalBookingAmounts`.
 *
 * Debe coincidir con server/api-util/cocopoolPricing.js, que es la fuente de verdad —
 * el servidor recalcula todo esto al crear la oferta y al construir los line items, y
 * el cliente nunca le manda importes, solo el precio tecleado. Esto existe únicamente
 * para la previsualización en vivo del modal, que no puede esperar a una llamada por
 * cada tecla. Un test fija los mismos ejemplos que el del servidor, así que si los dos
 * se separan, CI lo caza.
 *
 * A diferencia del servidor, devuelve `null` en vez de lanzar: aquí un precio a medio
 * escribir es lo normal, no un error.
 */
export const EXTERNAL_BOOKING_CUSTOMER_COMMISSION_PCT = 3;
export const EXTERNAL_BOOKING_PROVIDER_COMMISSION_PCT = 10;
export const EXTERNAL_BOOKING_PROVIDER_COMMISSION_FLAT_SUBUNITS = 3000;
export const EXTERNAL_BOOKING_PROVIDER_COMMISSION_THRESHOLD_SUBUNITS = 30000;

export function externalBookingAmounts(hostPriceSubunits) {
  const base = Number(hostPriceSubunits);
  if (!Number.isInteger(base) || base <= 0) {
    return null;
  }
  const providerCommissionSubunits =
    base >= EXTERNAL_BOOKING_PROVIDER_COMMISSION_THRESHOLD_SUBUNITS
      ? Math.round((base * EXTERNAL_BOOKING_PROVIDER_COMMISSION_PCT) / 100)
      : EXTERNAL_BOOKING_PROVIDER_COMMISSION_FLAT_SUBUNITS;
  if (base <= providerCommissionSubunits) {
    return null;
  }
  const customerCommissionSubunits = Math.round(
    (base * EXTERNAL_BOOKING_CUSTOMER_COMMISSION_PCT) / 100
  );
  return {
    hostPriceSubunits: base,
    customerCommissionSubunits,
    guestTotalSubunits: base + customerCommissionSubunits,
    providerCommissionSubunits,
    hostPayoutSubunits: base - providerCommissionSubunits,
  };
}
